import React from 'react';
import {observer} from 'mobx-react';
import {FilterTableStatus} from '../stores/DashboardStore';
import {classes, stylesheet} from 'typestyle';
import {spinner} from '../styles/style';
import {FilterName, isFilterValue, TopFilterData} from '../types/api/dashboard/DashboardFilters';
import {important, px} from 'csx';
import {formatValue, numberFormat, percentFormat} from '../utility/FormatValueUtils';
import {I18n} from '../i18n/i18n';
import {List, AutoSizer} from 'react-virtualized';
import {LightBlueColor, WhiteColor} from '../styles/colors';

interface DashboardTableFoldoutProps {
    filterTableData: FilterTableStatus;
    loadTableFoldoutData: () => void;
}

const FILTER_TABLE_ROW_HEIGHT = 30;

@observer
export default class DashboardTableFoldout extends React.Component<DashboardTableFoldoutProps> {
    public componentDidMount() {
        this.props.loadTableFoldoutData();
    }

    public componentDidUpdate() {
        this.props.loadTableFoldoutData();
    }

    private renderFilterRow = (index: number, style: React.CSSProperties, filters: FilterName[]) => {
        const filter = filters[index];
        return (
            <div className={classes(tableFoldoutCss.lazyFilterRow, index % 2 !== 0 ? 'colored' : undefined)} style={style} key={index}>
                <div className={tableFoldoutCss.lazyFilterRowText}>
                    {filter.filter} {isFilterValue(filter) ? `(${filter.value})` : null}
                </div>
                <div className={tableFoldoutCss.lazyFilterRowValue}>{formatValue(filter.count, numberFormat())}</div>
                <div className={tableFoldoutCss.lazyFilterRowValue}>{formatValue(filter.ratio, percentFormat(2))}</div>
            </div>
        );
    };

    private renderList(filterNames: FilterName[], title: React.ReactNode) {
        return (
            <React.Fragment>
                <div className={tableFoldoutCss.tableTitle}>{title}</div>
                <AutoSizer disableHeight>
                    {({width}) => (
                        <List
                            rowHeight={FILTER_TABLE_ROW_HEIGHT}
                            width={width}
                            height={Math.min(FILTER_TABLE_ROW_HEIGHT * filterNames.length, 210)}
                            rowCount={filterNames.length}
                            rowRenderer={({index, style}) => this.renderFilterRow(index, style, filterNames)}
                        />
                    )}
                </AutoSizer>
            </React.Fragment>
        );
    }

    private renderFilterTables(topFilterTable: TopFilterData) {
        return (
            <div className={tableFoldoutCss.filterContainer}>
                <div className={tableFoldoutCss.tableTitle}>
                    <I18n i18nKey="dashboard:overviewFoldout.generalFilterInformation" />
                </div>
                <table className={tableFoldoutCss.fullWidthTable}>
                    <tbody>
                        <tr className={classes(tableFoldoutCss.filterRow, 'noColor')}>
                            <td className={tableFoldoutCss.filterCell}>
                                <I18n i18nKey="dashboard:overviewFoldout.averageSearchFilterDepth" />
                            </td>
                            <td className={classes(tableFoldoutCss.filterCell, 'alignRight')}>
                                {formatValue(topFilterTable.searchFilterDepth, numberFormat(2))}
                            </td>
                        </tr>
                        <tr className={tableFoldoutCss.filterRow}>
                            <td className={tableFoldoutCss.filterCell}>
                                <I18n i18nKey="dashboard:overviewFoldout.searchFilterUsageRatio" />
                            </td>
                            <td className={classes(tableFoldoutCss.filterCell, 'alignRight')}>
                                {formatValue(topFilterTable.searchFilterUsageRatio, percentFormat())}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div className={classes(tableFoldoutCss.fullWidthTable, tableFoldoutCss.fixedTableHeight)}>
                    {this.renderList(topFilterTable.asn.filterNames, <I18n i18nKey="dashboard:overviewFoldout.topSearchFilterGroups" />)}
                    {this.renderList(topFilterTable.asn.filterValues, <I18n i18nKey="dashboard:overviewFoldout.topSearchFilterValues" />)}
                    {this.renderList(topFilterTable.navigation.filterNames, <I18n i18nKey="dashboard:overviewFoldout.topNavigationFilterGroups" />)}
                    {this.renderList(topFilterTable.navigation.filterValues, <I18n i18nKey="dashboard:overviewFoldout.topNavigationFilterValues" />)}
                </div>
            </div>
        );
    }

    private renderLoadingSpinner() {
        return (
            <div className={tableFoldoutCss.centeredLoadingSpinner}>
                <div className={tableFoldoutCss.loadingSpinner}>
                    <div className={spinner(50)} />
                </div>
            </div>
        );
    }

    private renderTopFilters() {
        return (
            <div>{this.props.filterTableData.isLoading ? this.renderLoadingSpinner() : this.renderFilterTables(this.props.filterTableData.data)}</div>
        );
    }

    public render() {
        return <div className={tableFoldoutCss.tableContainer}>{this.renderTopFilters()}</div>;
    }
}

const tableFoldoutCss = stylesheet({
    lazyFilterRowValue: {
        minWidth: 80,
        marginLeft: 15,
        whiteSpace: 'nowrap',
    },
    lazyFilterRowText: {
        flexGrow: 1,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    fixedTableHeight: {
        height: 'auto',
    },
    tableTitle: {
        whiteSpace: 'nowrap',
        marginTop: 15,
        backgroundColor: LightBlueColor,
        height: 40,
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
        borderRadius: 2,
        paddingLeft: 15,
    },
    fullWidthTable: {
        width: '100%',
    },
    filterCell: {
        padding: '4px 15px',
        $nest: {
            '&.alignRight': {
                textAlign: 'right',
            },
        },
    },
    lazyFilterRow: {
        display: 'flex',
        lineHeight: px(FILTER_TABLE_ROW_HEIGHT),
        padding: '0px 15px',
        $nest: {
            '&.colored': {
                backgroundColor: WhiteColor,
            },
        },
    },
    filterRow: {
        backgroundColor: WhiteColor,
        $nest: {
            '&.noColor': {
                backgroundColor: important('transparent'),
            },
        },
    },
    filterContainer: {
        paddingBottom: 15,
        textAlign: 'left',
    },
    centeredLoadingSpinner: {
        display: 'flex',
        justifyContent: 'center',
    },
    loadingSpinner: {
        borderRadius: 2,
        position: 'relative',
        height: 75,
        width: 75,
    },
    tableContainer: {
        position: 'relative',
    },
});



// WEBPACK FOOTER //
// ./src/views/DashboardTableFoldout.tsx